import { useEffect, useState } from "react";
import type {
	CustomTournament,
	CustomTournamentSelectionSettings,
} from "../../common/types.ts";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { toWorker } from "../util/toWorker.ts";
import { useLocal } from "../util/local.ts";

type EventType = "olympics" | "tournament" | "allStar";
type Country = { country: string; count: number };
type PreviewTeam = {
	id: number;
	name: string;
	players: { pid: number; name: string; tid: number; injured?: true }[];
};

const defaultSettings: CustomTournamentSelectionSettings = {
	rosterSize: 12,
	numCountries: 8,
	selectionFormula: "ovr + value / 10",
	includeInjured: false,
	balancePositions: true,
	minPlayersPerCountry: 12,
	format: "groups",
	teamSource: "countries",
	gamesPerMatchup: 1,
	timing: "beforeDraft",
	balanceMode: "none",
	balancePercent: 10,
};

const CustomTournament = ({
	countries,
	season,
	tournament,
}: {
	countries: Country[];
	season: number;
	tournament?: CustomTournament;
}) => {
	const { godMode } = useLocal(["godMode"]);
	const [type, setType] = useState<EventType>(
		tournament?.type === "allStar"
			? "allStar"
			: tournament?.type === "tournament"
				? "tournament"
				: "olympics",
	);
	const [name, setName] = useState(tournament?.name ?? "Olympics");
	const [recurrence, setRecurrence] = useState<CustomTournament["recurrence"]>(
		tournament?.recurrence ?? "once",
	);
	const [recurrenceInterval, setRecurrenceInterval] = useState(
		tournament?.recurrenceInterval ?? 2,
	);
	const [settings, setSettings] = useState<CustomTournamentSelectionSettings>(
		tournament?.selectionSettings ?? defaultSettings,
	);
	const [selectedCountries, setSelectedCountries] = useState<string[]>(
		tournament?.selectionSettings?.countries ?? [],
	);
	const [preview, setPreview] = useState<PreviewTeam[]>(
		tournament?.teams ?? [],
	);

	useTitleBar({ title: "Custom Tournament" });

	useEffect(() => {
		if (!tournament) {
			return;
		}
		setType(
			tournament.type === "allStar"
				? "allStar"
				: tournament.type === "tournament"
					? "tournament"
					: "olympics",
		);
		setName(tournament.name);
		setRecurrence(tournament.recurrence ?? "once");
		setRecurrenceInterval(tournament.recurrenceInterval ?? 2);
		setSettings({
			...defaultSettings,
			...tournament.selectionSettings,
			format: tournament.selectionSettings?.format ?? "knockout",
			teamSource: tournament.selectionSettings?.teamSource ?? "countries",
			gamesPerMatchup: tournament.selectionSettings?.gamesPerMatchup ?? 1,
			timing: tournament.selectionSettings?.timing ?? "beforeDraft",
			balanceMode: tournament.selectionSettings?.balanceMode ?? "none",
			balancePercent: tournament.selectionSettings?.balancePercent ?? 10,
		});
		setSelectedCountries(tournament.selectionSettings?.countries ?? []);
		setPreview(tournament.teams);
	}, [tournament]);

	const updateSettings = <K extends keyof CustomTournamentSelectionSettings>(
		key: K,
		value: CustomTournamentSelectionSettings[K],
	) => {
		setSettings((old) => ({ ...old, [key]: value }));
	};

	const getInput = () => ({
		type,
		recurrence,
		recurrenceInterval:
			recurrence === "everyNSeasons" ? recurrenceInterval : undefined,
		selectionSettings: {
			...settings,
			countries: selectedCountries.length > 0 ? selectedCountries : undefined,
		},
	});

	const handlePreview = async () => {
		const teams = await toWorker("main", "customTournamentPreview", getInput());
		setPreview(teams);
	};

	const handleSave = async () => {
		const saved = await toWorker("main", "customTournamentCreate", {
			name,
			...getInput(),
		});
		setPreview(saved.teams);
	};

	const isActive =
		tournament?.status === "inProgress" || tournament?.status === "complete";

	return (
		<div style={{ maxWidth: 1200 }}>
			<h2>Olympics &amp; Custom All-Star Games</h2>
			<p>
				Build national teams automatically. Players are selected from active
				players by country, ranked with your formula, and balanced by position.
				You choose the rules—not individual players.
			</p>
			{!godMode ? (
				<div className="alert alert-warning">
					Enable God Mode to create or modify a custom event.
				</div>
			) : null}
			{isActive ? (
				<div className="alert alert-info">
					<b>{tournament?.name}</b> is {tournament?.status}. The champion is{" "}
					{tournament?.championTeam === undefined
						? "not decided yet"
						: tournament.teams[tournament.championTeam]?.name}
					.
				</div>
			) : (
				<div className="card mb-4">
					<div className="card-body">
						<h3 className="card-title">Event builder</h3>
						<div className="row g-3">
							<div className="col-md-4">
								<label className="form-label" htmlFor="event-name">
									Event name
								</label>
								<input
									id="event-name"
									className="form-control"
									value={name}
									onChange={(event) => setName(event.target.value)}
								/>
							</div>
							<div className="col-md-4">
								<label className="form-label" htmlFor="event-recurrence">
									Repeat event
								</label>
								<select
									id="event-recurrence"
									className="form-select"
									value={recurrence}
									onChange={(event) =>
										setRecurrence(
											event.target.value as CustomTournament["recurrence"],
										)
									}
								>
									<option value="once">Once</option>
									<option value="everySeason">Every season</option>
									<option value="everyNSeasons">Every N seasons</option>
								</select>
							</div>
							{recurrence === "everyNSeasons" ? (
								<div className="col-md-4">
									<label className="form-label" htmlFor="recurrence-interval">
										Repeat every N seasons
									</label>
									<input
										id="recurrence-interval"
										className="form-control"
										min={1}
										type="number"
										value={recurrenceInterval}
										onChange={(event) =>
											setRecurrenceInterval(Number(event.target.value))
										}
									/>
								</div>
							) : null}
							<div className="col-md-4">
								<label className="form-label" htmlFor="event-type">
									Event type
								</label>
								<select
									id="event-type"
									className="form-select"
									value={type}
									onChange={(event) => {
										const value = event.target.value as EventType;
										setType(value);
										if (value === "allStar") {
											updateSettings("numCountries", 2);
											updateSettings("format", "knockout");
										}
									}}
								>
									<option value="tournament">Custom tournament</option>
									<option value="olympics">Olympics national teams</option>
									<option value="allStar">Custom All-Star Game</option>
								</select>
							</div>
							<div className="col-md-4">
								<label className="form-label" htmlFor="event-teams">
									{type === "allStar" ? "Players per team" : "Teams/countries"}
								</label>
								<select
									id="event-teams"
									className="form-select"
									value={
										type === "allStar"
											? settings.rosterSize
											: settings.numCountries
									}
									onChange={(event) =>
										updateSettings(
											type === "allStar" ? "rosterSize" : "numCountries",
											Number(event.target.value),
										)
									}
								>
									{(type === "allStar" ? [5, 8, 10, 12, 15] : [4, 8, 16]).map(
										(value) => (
											<option key={value} value={value}>
												{value}
											</option>
										),
									)}
								</select>
							</div>
							{type !== "allStar" ? (
								<div className="col-md-4">
									<label className="form-label" htmlFor="team-source">
										Automatic team source
									</label>
									<select
										id="team-source"
										className="form-select"
										value={settings.teamSource}
										onChange={(event) =>
											updateSettings(
												"teamSource",
												event.target.value as "countries" | "leagueTeams",
											)
										}
									>
										<option value="countries">Nationalities/countries</option>
										<option value="leagueTeams">Current league teams</option>
									</select>
								</div>
							) : null}
							{type !== "allStar" ? (
								<div className="col-md-4">
									<label className="form-label" htmlFor="roster-size">
										Players per country
									</label>
									<input
										id="roster-size"
										className="form-control"
										min={1}
										max={30}
										type="number"
										value={settings.rosterSize}
										onChange={(event) =>
											updateSettings("rosterSize", Number(event.target.value))
										}
									/>
								</div>
							) : null}
							{type !== "allStar" ? (
								<div className="col-md-4">
									<label className="form-label" htmlFor="event-format">
										Tournament format
									</label>
									<select
										id="event-format"
										className="form-select"
										value={settings.format}
										onChange={(event) =>
											updateSettings(
												"format",
												event.target.value as
													| "groups"
													| "knockout"
													| "roundRobin",
											)
										}
									>
										<option value="groups">Group stage + knockout</option>
										<option value="knockout">Single elimination</option>
										<option value="roundRobin">Round robin</option>
									</select>
								</div>
							) : null}
							<div className="col-md-8">
								<label className="form-label" htmlFor="selection-formula">
									Automatic selection formula
								</label>
								<input
									id="selection-formula"
									className="form-control font-monospace"
									value={settings.selectionFormula}
									onChange={(event) =>
										updateSettings("selectionFormula", event.target.value)
									}
								/>
								<small className="text-body-secondary">
									Available variables: <code>ovr</code>, <code>pot</code>,{" "}
									<code>value</code>, <code>age</code>, <code>games</code>,{" "}
									<code>experience</code>, <code>injury</code>, plus{" "}
									<code>ratings.*</code> and <code>stats.*</code>. Functions:{" "}
									<code>min</code>, <code>max</code>, <code>abs</code>.
								</small>
							</div>
							<div className="col-md-4">
								<label className="form-label" htmlFor="games-per-matchup">
									Games per matchup
								</label>
								<input
									id="games-per-matchup"
									className="form-control"
									min={1}
									max={7}
									type="number"
									value={settings.gamesPerMatchup}
									onChange={(event) =>
										updateSettings(
											"gamesPerMatchup",
											Number(event.target.value),
										)
									}
								/>
							</div>
							<div className="col-md-4">
								<label className="form-label" htmlFor="event-timing">
									When should it happen?
								</label>
								<select
									id="event-timing"
									className="form-select"
									value={settings.timing}
									onChange={(event) =>
										updateSettings(
											"timing",
											event.target
												.value as CustomTournamentSelectionSettings["timing"],
										)
									}
								>
									<option value="afterPreseason">After preseason</option>
									<option value="regularSeasonDay">Regular-season day</option>
									<option value="beforePlayoffs">Before playoffs</option>
									<option value="betweenPlayoffRounds">
										Between playoff rounds
									</option>
									<option value="beforeDraft">Before the draft lottery</option>
								</select>
							</div>
							{settings.timing === "regularSeasonDay" ? (
								<div className="col-md-4">
									<label className="form-label" htmlFor="timing-day">
										Regular-season day
									</label>
									<input
										id="timing-day"
										className="form-control"
										min={1}
										type="number"
										value={settings.timingDay ?? 1}
										onChange={(event) =>
											updateSettings("timingDay", Number(event.target.value))
										}
									/>
								</div>
							) : null}
							<div className="col-md-4">
								<label className="form-label" htmlFor="balance-mode">
									Olympic balance mode
								</label>
								<select
									id="balance-mode"
									className="form-select"
									value={settings.balanceMode}
									onChange={(event) =>
										updateSettings(
											"balanceMode",
											event.target
												.value as CustomTournamentSelectionSettings["balanceMode"],
										)
									}
								>
									<option value="none">None</option>
									<option value="roster">Roster balance only</option>
									<option value="light">
										Light temporary strength compression
									</option>
								</select>
							</div>
							{settings.balanceMode === "light" ? (
								<div className="col-md-4">
									<label className="form-label" htmlFor="balance-percent">
										Maximum compression (%)
									</label>
									<input
										id="balance-percent"
										className="form-control"
										min={0}
										max={25}
										type="number"
										value={settings.balancePercent}
										onChange={(event) =>
											updateSettings(
												"balancePercent",
												Number(event.target.value),
											)
										}
									/>
								</div>
							) : null}
							<div className="col-md-4">
								<label className="form-label" htmlFor="min-country-players">
									Minimum country pool
								</label>
								<input
									id="min-country-players"
									className="form-control"
									min={1}
									type="number"
									value={settings.minPlayersPerCountry}
									onChange={(event) =>
										updateSettings(
											"minPlayersPerCountry",
											Number(event.target.value),
										)
									}
								/>
							</div>
						</div>
						<div className="form-check mt-3">
							<input
								id="include-injured"
								className="form-check-input"
								type="checkbox"
								checked={settings.includeInjured}
								onChange={(event) =>
									updateSettings("includeInjured", event.target.checked)
								}
							/>
							<label className="form-check-label" htmlFor="include-injured">
								Allow injured players
							</label>
						</div>
						<div className="form-check">
							<input
								id="balance-positions"
								className="form-check-input"
								type="checkbox"
								checked={settings.balancePositions}
								onChange={(event) =>
									updateSettings("balancePositions", event.target.checked)
								}
							/>
							<label className="form-check-label" htmlFor="balance-positions">
								Balance positions while selecting
							</label>
						</div>
						{type !== "allStar" && settings.teamSource === "countries" ? (
							<div className="mt-3">
								<label className="form-label" htmlFor="countries">
									Countries (optional; leave empty for strongest available
									countries)
								</label>
								<select
									id="countries"
									className="form-select"
									multiple
									size={8}
									value={selectedCountries}
									onChange={(event) =>
										setSelectedCountries(
											Array.from(
												event.target.selectedOptions,
												(option) => option.value,
											),
										)
									}
								>
									{countries.map(({ country, count }) => (
										<option key={country} value={country}>
											{country} ({count} active players)
										</option>
									))}
								</select>
							</div>
						) : null}
						<div className="mt-3">
							<button
								className="btn btn-secondary me-2"
								disabled={!godMode}
								onClick={handlePreview}
							>
								Generate automatic preview
							</button>
							<button
								className="btn btn-primary"
								disabled={!godMode}
								onClick={handleSave}
							>
								Save event
							</button>
						</div>
					</div>
				</div>
			)}

			<h3>Automatic roster preview</h3>
			<div className="row g-3">
				{preview.map((team) => (
					<div className="col-md-6 col-lg-4" key={team.id}>
						<div className="card h-100">
							<div className="card-header">
								<b>{team.name}</b> ({team.players.length})
							</div>
							<ul className="list-group list-group-flush">
								{team.players.map((player) => (
									<li className="list-group-item" key={player.pid}>
										{player.name}
										{player.injured ? " (injured)" : ""}
									</li>
								))}
							</ul>
						</div>
					</div>
				))}
			</div>
			<small className="text-body-secondary d-block mt-3">
				Season {season}. Timing: {settings.timing}. Generated rosters are saved
				when the event is activated.
			</small>
		</div>
	);
};

export default CustomTournament;
